import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  PlusCircleIcon,
  TrashIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/solid";
import { addQuiz } from "../../State/Quiz/Action";
import SuccessDialog from "../Tools/SuccessDialog";
import FailureDialog from "../Tools/FailureDialog";

const MAX_OPTIONS = 5;
const MIN_OPTIONS = 2;

const Quiz = () => {
  const { chapterId } = useParams();
  const dispatch = useDispatch();

  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState(["", ""]);
  const [correct, setCorrect] = useState([false, false]);
  const [multipleAnswers, setMultipleAnswers] = useState(false);
  const [explanation, setExplanation] = useState("");
  const [validationError, setValidationError] = useState("");
  const addQuizResult = useSelector((store) => store.quiz?.addQuizResult);
  const addQuizError = useSelector((store) => store.quiz?.error);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showFailureDialog, setShowFailureDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const location = useLocation(); // used for query params

  // Extract moduleId from query string (e.g., ?moduleId=abc123)
  const queryParams = new URLSearchParams(location.search);
  const moduleId = queryParams.get("moduleId");

  const navigate = useNavigate();

  const handleOptionChange = (index, value) => {
    const updated = [...options];
    updated[index] = value.slice(0, 128);
    setOptions(updated);
  };

  const handleCorrectChange = (index) => {
    const updated = multipleAnswers
      ? correct.map((c, i) => (i === index ? !c : c))
      : correct.map((_, i) => i === index);
    setCorrect(updated);
  };

  const addOption = () => {
    if (options.length < MAX_OPTIONS) {
      setOptions([...options, ""]);
      setCorrect([...correct, false]);
    }
  };

  const deleteOption = (index) => {
    const updatedOptions = [...options];
    const updatedCorrect = [...correct];
    updatedOptions.splice(index, 1);
    updatedCorrect.splice(index, 1);
    setOptions(updatedOptions);
    setCorrect(updatedCorrect);
  };

  const resetForm = () => {
    setQuestion("");
    setOptions(["", ""]);
    setCorrect([false, false]);
    setMultipleAnswers(false);
    setExplanation("");
    setValidationError("");
  };

  const handleAddQuiz = () => {
    const isAnyOptionCorrect = correct.some((val) => val);
    if (!isAnyOptionCorrect) {
      setValidationError("Choose at least one option");
      return;
    }
    setValidationError("");

    const input = {
      chapterId,
      question,
      optionA: options[0] || "",
      optionB: options[1] || "",
      optionC: options[2] || "",
      optionD: options[3] || "",
      optionE: options[4] || "",
      multipleAnswers,
      optionACorrect: correct[0] || false,
      optionBCorrect: correct[1] || false,
      optionCCorrect: correct[2] || false,
      optionDCorrect: correct[3] || false,
      optionECorrect: correct[4] || false,
      explanation,
    };
    dispatch(addQuiz(chapterId, input));
  };

  useEffect(() => {
    if (addQuizResult?.success) {
      setShowSuccessDialog(true);
      setDialogMessage(addQuizResult.message || "Quiz added successfully!");
      resetForm();
    }
    if (addQuizError !== null && addQuizError?.data === null) {
      setShowFailureDialog(true);
      setDialogMessage(addQuizError.message || "Failed to add quiz.");
    }
  }, [addQuizResult, addQuizError]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 to-indigo-200 p-6">
      <h1 className="text-4xl font-bold text-indigo-800 mb-6 text-center">
        🧠 Create Quiz Question
      </h1>

      <div className="bg-white shadow-lg rounded-xl p-6 mb-6 border border-indigo-300">
        <label className="block text-lg font-semibold text-indigo-700 mb-2">
          Question
        </label>
        <textarea
          className="w-full border border-gray-300 p-3 rounded-md mb-4 resize-none"
          rows={3}
          placeholder="Enter your question (max 512 characters)"
          value={question}
          onChange={(e) => setQuestion(e.target.value.slice(0, 512))}
        />

        <div className="mb-4">
          <span className="block font-semibold text-gray-700 mb-1">
            Answer Type:
          </span>
          <div className="flex gap-4">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="answerType"
                checked={!multipleAnswers}
                onChange={() => {
                  setMultipleAnswers(false);
                  setCorrect(correct.map(() => false));
                }}
              />
              Single Correct Answer
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="answerType"
                checked={multipleAnswers}
                onChange={() => {
                  setMultipleAnswers(true);
                  setCorrect(correct.map(() => false));
                }}
              />
              Multiple Correct Answers
            </label>
          </div>
        </div>

        {options.map((opt, i) => (
          <div
            key={i}
            className="flex items-center gap-3 mb-2 bg-gray-50 p-2 rounded-md"
          >
            <input
              type={multipleAnswers ? "checkbox" : "radio"}
              name="correctOption"
              checked={correct[i] || false}
              onChange={() => handleCorrectChange(i)}
            />
            <input
              type="text"
              className="flex-1 border border-gray-300 p-2 rounded-md"
              placeholder={`Option ${String.fromCharCode(65 + i)}`}
              value={opt}
              onChange={(e) => handleOptionChange(i, e.target.value)}
            />
            {options.length > MIN_OPTIONS && (
              <button
                type="button"
                onClick={() => deleteOption(i)}
                className="text-red-500 hover:text-red-700"
                title="Remove Option"
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            )}
          </div>
        ))}

        {options.length < MAX_OPTIONS && (
          <button
            type="button"
            onClick={addOption}
            className="flex items-center gap-2 text-indigo-600 font-medium mt-2 hover:underline"
          >
            <PlusCircleIcon className="h-5 w-5" />
            Add More Option
          </button>
        )}

        <div className="mt-4">
          <label className="block font-semibold text-gray-700 mb-1">
            Explanation (Optional):
          </label>
          <textarea
            className="w-full border border-gray-300 p-2 rounded-md"
            rows={2}
            placeholder="Add explanation (optional, max 512 characters)"
            value={explanation}
            onChange={(e) => setExplanation(e.target.value.slice(0, 512))}
          />
        </div>

        {validationError && (
          <p className="text-red-600 text-center font-semibold mt-4">
            {validationError}
          </p>
        )}
      </div>

      <div className="flex flex-col md:flex-row justify-center items-center gap-4">
        <button
          onClick={handleAddQuiz}
          className="flex items-center gap-2 bg-indigo-600 text-white px-6 py-2 rounded-full hover:bg-indigo-700 shadow-md"
        >
          <PlusCircleIcon className="h-5 w-5" />
          Add Next Quiz
        </button>

        <button
          onClick={() => {
            if (correct.some((c) => c)) {
              setValidationError("");
              handleAddQuiz();
              // Optionally navigate to summary page here
              // navigate("/quiz-summary");
              navigate(`/quiz-summary/${chapterId}?moduleId=${moduleId}`);
            } else {
              setValidationError("Choose at least one option");
            }
          }}
          className="flex items-center gap-2 bg-green-600 text-white px-6 py-2 rounded-full hover:bg-green-700 shadow-md"
        >
          <CheckCircleIcon className="h-5 w-5" />
          Finish Quiz
        </button>
      </div>

      <SuccessDialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        message={dialogMessage}
      />
      <FailureDialog
        open={showFailureDialog}
        onClose={() => setShowFailureDialog(false)}
        message={dialogMessage}
      />
    </div>
  );
};

export default Quiz;
